/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import { slug } from '../../../common';
import LeaderboardSidebar from './LeaderboardSidebar';
import LeaderboardToolbar from './LeaderboardToolbar';
import LeaderboardItemList from './LeaderboardItemList';
import LeaderboardPodium from './LeaderboardPodium';
import LeaderboardState from '../states/LeaderboardState';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';
import LeaderboardHero from './LeaderboardHero';
import ItemList from 'flarum/common/utils/ItemList';
const t = function () {
  return app.translator.trans(...arguments);
};
const prfx = `${slug}.forum.leaderboard`;
export default class LeaderboardPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    app.history.push('leaderboard');
    this.bodyClass = 'App--leaderboard';
    app.setTitle(t(`${prfx}.title`));
    if (!app.leaderboard) {
      app.leaderboard = new LeaderboardState();
    }
    const requested = m.route.param('activityType');
    const params = {
      timeSpan: m.route.param('timeSpan') || 'month'
    };
    const groupFilter = m.route.param('group');
    if (groupFilter) {
      params.group = groupFilter;
    }
    const levelFilter = m.route.param('level');
    if (levelFilter) {
      params.level = levelFilter;
    }
    this.requested = requested;
    this.boardParams = params;
    this.load(requested, params);
  }
  onremove(vnode) {
    super.onremove(vnode);

    // Every route change mounts a new page (DefaultResolver keys on the params), so a
    // continuation that outlives this one belongs to a board the reader has left.
    this.removed = true;
  }
  load(requested, params, force) {
    if (force === void 0) {
      force = false;
    }
    app.leaderboard.resolveActivityType(requested).then(activityType => {
      if (this.removed) {
        return;
      }
      if (!activityType) {
        // A board the actor may not open — bookmarked /leaderboard/exp while exp is
        // off — falls back to the default board instead of a permission error. A list
        // that failed to load says nothing about the board, so it stays put.
        if (requested && !app.leaderboard.activitiesFailed) {
          this.redirectToDefaultBoard();
        }
        return;
      }
      const state = app.leaderboard;
      const boardParams = {
        ...params,
        activityType
      };

      // A recovery reloads even when the board survives the refetch: after a 403 on
      // loadNext the loaded pages make refreshParams() a no-op.
      if (force) {
        state.params = boardParams;
      }
      (force ? state.refresh() : state.refreshParams(boardParams)).catch(error => this.recoverFromForbidden(error, requested, params))
      // The state clears its loading flags after its own last redraw.
      .finally(() => m.redraw());
    });
  }

  /**
   * The state leaves a 403 unalerted. The first one per mount is taken to mean the cached
   * board list predates an admin hiding this board, and refetches it; that refetch is the
   * loop bound. Any later 403 is a real denial — `viewLeaderboards` revoked, gamification
   * switched off — and replaces the board, which would otherwise read as merely empty.
   */
  recoverFromForbidden(error, requested, params) {
    if (this.removed || error?.status !== 403) {
      return;
    }
    if (this.refetchedActivities) {
      this.forbidden = true;
      return;
    }
    this.refetchedActivities = true;
    app.leaderboard.invalidateActivities();
    this.load(requested, params, true);
  }
  redirectToDefaultBoard() {
    m.route.set(app.route('leaderboard'), null, {
      replace: true
    });
  }
  hasNoBoards() {
    const state = app.leaderboard;
    return !state.resolving && state.activitiesRequest !== null && state.activities.length === 0;
  }
  activitiesFailed() {
    return !app.leaderboard.resolving && app.leaderboard.activitiesFailed;
  }
  isForbidden() {
    return this.forbidden || this.activitiesFailed() && app.leaderboard.activitiesForbidden;
  }
  view() {
    return m("div", {
      className: "LeaderboardPage"
    }, this.heroViewItems().toArray(), m("div", {
      className: "container"
    }, m("div", {
      className: "sideNavContainer"
    }, m("nav", {
      className: "LeaderboardPage-nav sideNav"
    }, this.navViewItems().toArray()), m("div", {
      className: "LeaderboardPage-content sideNavOffset"
    }, this.contentViewItems().toArray()))));
  }
  heroViewItems() {
    const items = new ItemList();
    items.add('leaderboard-hero', m(LeaderboardHero, null), 50);
    return items;
  }
  navViewItems() {
    const items = new ItemList();
    items.add('sidebar', this.sidebarView(), 80);
    return items;
  }
  sidebarView() {
    return m(LeaderboardSidebar, null);
  }
  contentViewItems() {
    const items = new ItemList();
    if (this.isForbidden()) {
      items.add('forbidden', m(Placeholder, {
        text: app.translator.trans('core.lib.error.permission_denied_message')
      }), 80);
      return items;
    }
    if (this.activitiesFailed()) {
      items.add('activitiesFailed', m(Placeholder, {
        text: t(`${prfx}.load_failed_text`)
      }), 80);
      return items;
    }
    if (this.hasNoBoards()) {
      items.add('noBoards', m(Placeholder, {
        text: t(`${prfx}.no_leaderboards_text`)
      }), 80);
      return items;
    }
    items.add('toolbar', this.toolbarView(), 80);
    items.add('podium', this.podiumView(), 60);
    items.add('itemList', this.itemListView(), 40);
    items.add('loadMore', this.loadMoreView(), 20);
    return items;
  }
  toolbarView() {
    return m(LeaderboardToolbar, null);
  }
  podiumView() {
    return m(LeaderboardPodium, null);
  }
  itemListView() {
    return m(LeaderboardItemList, null);
  }
  loadMoreView() {
    const state = app.leaderboard;
    let loading;
    if (state.isInitialLoading() || state.isLoadingNext()) {
      loading = m(LoadingIndicator, null);
    } else if (state.hasNext()) {
      loading = m(Button, {
        className: "Button Button--primary",
        onclick: () => state.loadNext().catch(error => this.recoverFromForbidden(error, this.requested, this.boardParams)).finally(() => m.redraw())
      }, t(`${prfx}.load_more_button`));
    }
    if (state.isEmpty()) {
      loading = m(Placeholder, {
        text: t(`${prfx}.empty_text`)
      });
    }
    return m("div", {
      className: "LeaderboardPage-loadMore"
    }, loading);
  }
}
flarum.reg.add('glowingblue-gamilytics', 'forum/leaderboards/components/LeaderboardPage', LeaderboardPage);